import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { IconButton, Icons } from 'utoniq-ui'

import { Item, PreSaleStatus } from '@/clients/utoniq-core/schema'
import { ThroughTicketTag } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, Organism, OrganismFrame } from '@/core/architecture'
import { DateTime } from '@/core/entities'

import { ApplyPreSaleFirstComeFirstServedValue } from '../FirstComeFirstServedDetailForm'

type TicketSelectModalProps = {
  isBeforeReceiption: boolean
  items: Array<Partial<Item>>
  selectedItems: ApplyPreSaleFirstComeFirstServedValue['choices']
  onClose: () => void
  preSaleStatus: PreSaleStatus
  onSelectItem: (item: Partial<Item> | undefined) => void
}

export const TicketSelectModal: Organism<TicketSelectModalProps> = ({
  isBeforeReceiption,
  items,
  onClose,
  onSelectItem,
}) => {
  const { t } = useTranslation('firstComeFirstServedDetail')
  const { router, generatePath, routerPages } = useRouter()

  const itemSkus = useMemo(() => {
    return items
      .map(item => {
        return {
          id: item.id,
          title: item.title,
          skuUnitIds: item.skuSettings?.units?.map(unit => unit.id) ?? [],
          skuUnits: item.skuSettings?.units,
          item,
          formattedPrice: item.formattedPrice,
          isThroughTicket: item.metadata?.ticket?.isThroughTicket,
        }
      })
      .flat()
  }, [items])

  return (
    <OrganismFrame
      sx={{
        bgcolor: 'background.default',
        height: '75dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" sx={{ py: 4, px: 3 }} alignItems="center">
        <Box width={30} />
        <Typography variant="h6" align="center" width="100%">
          {t('selectTicketTitle', 'チケットを選択')}
        </Typography>
        <IconButton.Close size="small" onClick={onClose} />
      </Stack>
      <Divider />
      <Stack
        flex={1}
        spacing={2}
        sx={{ p: 5, overflowY: 'auto', ...styleSets.hideScrollbar() }}
      >
        {isBeforeReceiption && (
          <Typography color="text.secondary" variant="caption">
            {t(
              'beforeReceiptionNote',
              '購入期間前のためチケットは選択できません。'
            )}
          </Typography>
        )}
        {itemSkus?.map(itemSku => {
          const item = itemSku?.item
          const purchaseAvailableNumber =
            (item?.issuedNumber ?? 0) -
            ((item?.purchasedNumber ?? 0) + (item?.pendingPurchaseNumber ?? 0))
          const haveItemSku = !!itemSku.skuUnitIds.length
          const isSoldOut =
            item?.issuedNumber !== null
              ? item?.issuedNumber ===
                (item?.purchasedNumber ?? 0) +
                  (item?.pendingPurchaseNumber ?? 0)
              : false

          const isHaveOneOfSkuSoldOut = checkHaveSoldOut(item, haveItemSku)

          const resaleAvailable = checkResaleAvailable(item)

          const disabledCard =
            (resaleAvailable ? isSoldOut && !haveItemSku : isSoldOut) ||
            isBeforeReceiption

          return (
            <Stack key={itemSku?.id} sx={{ position: 'relative' }}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  bgcolor: disabledCard ? 'grey.100' : 'grey.50',
                }}
              >
                <CardActionArea
                  disabled={disabledCard}
                  onClick={() =>
                    onSelectItem(items.find(item => item.id === itemSku?.id))
                  }
                >
                  <CardContent
                    sx={{ px: 20, py: 4, '&:last-child': { pb: 4 } }}
                  >
                    <Typography
                      variant="subtitle1"
                      color={disabledCard ? 'text.disabled' : 'text.primary'}
                    >
                      {itemSku?.title}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {itemSku?.isThroughTicket && (
                        <ThroughTicketTag
                          disabled={
                            (isSoldOut && !resaleAvailable) ||
                            isBeforeReceiption
                          }
                        />
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ pt: 2 }}
                    >
                      <Typography
                        variant="h6"
                        color={disabledCard ? 'text.disabled' : 'text.primary'}
                      >
                        ¥{itemSku?.formattedPrice}
                      </Typography>
                      <TicketStatusCell
                        amount={purchaseAvailableNumber}
                        isInfinite={item.issuedNumber === null}
                      />
                    </Stack>

                    {isHaveOneOfSkuSoldOut && resaleAvailable && (
                      <Stack justifyContent="flex-end" alignItems="end" pt={1}>
                        <Typography variant="subtitle2" color="primary.main">
                          {t('resaleAvailable')}
                        </Typography>
                      </Stack>
                    )}
                  </CardContent>
                </CardActionArea>
                {isSoldOut && resaleAvailable && !haveItemSku && (
                  <Stack
                    justifyContent="flex-end"
                    alignItems="end"
                    sx={{ mt: 3 }}
                  >
                    <Button
                      variant="text"
                      onClick={() => {
                        router.push({
                          pathname: generatePath(
                            routerPages.ResaleApplicationDetail,
                            {
                              itemId: item?.id ?? '#',
                            }
                          ),
                        })
                      }}
                      sx={{
                        p: 0,
                        position: 'absolute',
                        right: '20px',
                        bottom: '8px',
                        zIndex: 1,
                      }}
                    >
                      <Typography variant="subtitle2" color="primary.main">
                        {t('toResale', 'リセール情報に移動する')}
                      </Typography>
                      <Icons.RightArrow
                        color="primary"
                        fontSize="xSmall"
                        sx={{ ml: 2 }}
                      />
                    </Button>
                  </Stack>
                )}
              </Card>
            </Stack>
          )
        })}
      </Stack>
    </OrganismFrame>
  )
}
type TicketStatusCellProps = {
  amount: number
  isInfinite: boolean
}
const TicketStatusCell: Cell<TicketStatusCellProps> = ({
  amount,
  isInfinite,
}) => {
  const { t } = useTranslation('firstComeFirstServedDetail')

  if (isInfinite || amount >= 10) {
    return (
      <Typography variant="subtitle1" color="text.secondary">
        {t('onSale')}
      </Typography>
    )
  }

  if (amount >= 1 && amount < 10) {
    return (
      <Typography variant="subtitle1" color="text.secondary">
        {t('fewLeft')}
      </Typography>
    )
  }

  return (
    <Typography variant="subtitle1" color="error.main">
      {t('soldOutLabel')}
    </Typography>
  )
}

const checkHaveSoldOut = (item: Partial<Item>, haveItemSku: boolean) => {
  if (!haveItemSku) return false
  return item?.skuSettings?.units?.some(skuUnit =>
    skuUnit?.stock !== null
      ? skuUnit?.stock ===
        (skuUnit?.purchasedNumber ?? 0) + (skuUnit?.pendingPurchaseNumber ?? 0)
      : false
  )
}

const checkResaleAvailable = (item: Partial<Item>) => {
  return (
    item?.resaleEnabled &&
    item?.metadata?.ticket?.resalePeriod?.endedAt >
      DateTime.nowUnixMilliseconds() &&
    item?.metadata?.ticket?.resalePeriod?.startedAt <
      DateTime.nowUnixMilliseconds()
  )
}
